import { InputAdornment, styled } from "@mui/material";
import {
	DateTimeField,
	DateTimePicker as MuiDateTimePicker,
	type PickersActionBarAction,
	type TimeStepOptions,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isValid, parseISO } from "date-fns";
import { cs } from "date-fns/locale/cs";
import { Calendar } from "lucide-react";
import { type ReactElement, useState } from "react";
import { StyledValidationText } from "web_myplann/Forms/Components/FormStyles";
import type { ExtractProps } from "web_myplann/Infrastructure/Utils/ObjectUtils";
import { datePickerFieldStyles } from "web_myplann/Shared/Inputs/DatePickers/Styles";

const StyledFormInput = styled("div")<{
	$hasValue?: boolean;
}>`
	flex-grow: 1;
	min-width: 0;

	& > div {
		display: flex;
	}

  .MuiFormControl-root {  
    .MuiFormLabel-root {
      top: ${({ $hasValue }) => (!$hasValue ? "-4px" : "13px")};
      font-size: 15px;
      color: ${({ theme }) => theme.palette.deprecated.grey[400]};
      
      &.MuiInputLabel-shrink  {
        top: 13px;
      }
    }

    .MuiInputBase-root {
      border-radius: 12px;
      height: 52px;

      .MuiInputBase-input {
        position: relative;
        top: 8px;
        font-size: 14px;
        font-weight: 400;
      }

      svg {
        color: ${({ theme }) => theme.palette.deprecated.grey[500]};
      }
    }
  }
`;

type DateTimePickerProps = ExtractProps<typeof DateTimeField>;

export type Props = {
	label?: string | ReactElement;
	value: Date | null;
	hasError?: boolean;
	disabledPast?: boolean;
	disableFuture?: boolean;
	errorMessage?: string;
	formControlClass?: "long" | "medium" | "tiny";
	disabled?: boolean;
	minDateTime?: Date;
	timeSteps?: TimeStepOptions;
	minutesStep?: number;
	readOnly?: boolean;
	clearable?: boolean;
	onChange?: (date: Date | null) => void;
};

const StyledDateTimeField = styled(DateTimeField)`
  ${() => {
		return datePickerFieldStyles;
	}}
`;

type FieldProps = DateTimePickerProps & { readOnly?: boolean };

const Field: React.FunctionComponent<FieldProps> = (props) => {
	const { readOnly, ...rest } = props;
	return <StyledDateTimeField readOnly={readOnly} {...rest} />;
};

export const DateTimePicker: React.FunctionComponent<Props> = (props) => {
	const {
		label,
		value,
		hasError,
		errorMessage,
		disabledPast,
		disableFuture,
		disabled,
		onChange,
		minDateTime,
		timeSteps,
		minutesStep,
		readOnly,
		clearable,
	} = props;

	const [internalValue, setInternalValue] = useState<Date | null>(null);

	const handleChange = (date: Date | null) => {
		onChange?.(isValid(date) ? date : null);
	};

	let formattedValue = null;

	if (value) {
		formattedValue = value instanceof Date ? value : parseISO(value);
	}

	const actions: PickersActionBarAction[] = ["accept", "cancel"];

	if (clearable) {
		actions.push("clear");
	}

	return (
		<StyledFormInput $hasValue={!!formattedValue}>
			<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
				<MuiDateTimePicker
					label={label}
					format="d.M.yyyy HH:mm"
					value={formattedValue}
					onAccept={(value) => handleChange(value as Date)}
					onChange={(value) => setInternalValue(value as Date)}
					disablePast={disabledPast}
					disableFuture={disableFuture}
					disabled={disabled}
					minDateTime={minDateTime}
					timeSteps={timeSteps}
					minutesStep={minutesStep}
					slots={{ field: Field, openPickerIcon: Calendar }}
					slotProps={{
						actionBar: {
							actions,
							sx: {
								"& .MuiButton-root": {
									marginRight: 0,
								},
								"& .MuiButton-root:hover": {
									opacity: "0.7",
								},
								"& .MuiButton-root:nth-of-type(2)": {
									backgroundColor: "transparent",
									border: "1px solid #CBD5E1",
								},
								"& .MuiButton-root:nth-of-type(3)": {
									backgroundColor: "transparent",
									border: "1px solid #CBD5E1",
								},
							},
						},
						field: { readOnly },
						desktopPaper: {
							className: "react-module",
						},
						mobilePaper: {
							className: "react-module",
						},
						textField: {
							error: false,
							helperText: "",
							onBlur: () => {
								handleChange(internalValue);
							},
							InputProps: {
								endAdornment: (
									<InputAdornment position="end">
										<Calendar />
									</InputAdornment>
								),
							},
						},
						toolbar: {
							sx: {
								"& .MuiDateTimePickerToolbar-timeDigitsContainer": {
									alignItems: "center",
									"& .MuiDateTimePickerToolbar-separator": {
										margin: "0 4px",
									},
								},
							},
						},
					}}
				/>

				{hasError && (
					<StyledValidationText>{errorMessage}</StyledValidationText>
				)}
			</LocalizationProvider>
		</StyledFormInput>
	);
};
